// ./src/utils/api/BulkJobService.ts
//
// ROADMAP #132 — BACKGROUND JOBS: long work handed WHOLE to the server and done there, whether or not anybody is still
// watching (a guest import today; other imports and exports later — owner: "make sure the new table could be reused for
// import export of other kinds of entities"). A kind's own service starts one (`EventGuestService.startImport`) and gets
// the job back at once; the screens read it (`listFor`) for its progress and report, and `dismiss` the report.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { Pagination, SortItem } from "./commonApiTypes";
import { listAllPages } from "./listAllPages";

/** What a job does. Mirrors `BULK_JOB_KIND` in the server's `boundary/bulkJob.ts` — extend both together. */
export const BULK_JOB_KIND = {
  GUEST_IMPORT: "guest_import",
  /** B24.5 — a newly published event emailed to its organizer's followers. No screen lists it yet. */
  FOLLOWER_ANNOUNCE: "follower_announce",
  /** #271 — a member list from a spreadsheet (account-wide), shown on the Memberships page. */
  MEMBERSHIP_IMPORT: "membership_import",
  /** #288 — every subscription running on an account or a page, stopped before it closes or goes. */
  RECURRING_STOP: "recurring_stop",
  /** #296 — the host revoked us from Stripe: each subscription checked and marked, its donor told. No screen follows it. */
  RECURRING_DISCONNECT: "recurring_disconnect",
} as const;

export type BulkJobKind = (typeof BULK_JOB_KIND)[keyof typeof BULK_JOB_KIND];

/** Where a job is. Mirrors `BULK_JOB_STATE` on the server. */
export const BULK_JOB_STATE = {
  QUEUED: "queued",
  RUNNING: "running",
  DONE: "done",
  FAILED: "failed",
} as const;

/** Codes the shared machinery writes (a row's reason, a failed job's lastError). Mirrors `BULK_JOB_REASON` on the server. */
export const BULK_JOB_REASON = {
  GAVE_UP: "gave_up",
  SCOPE_GONE: "scope_gone",
  FINISH_FAILED: "finish_failed",
} as const;

export const bulkJobIsOpen = (state: string | null | undefined): boolean => state === BULK_JOB_STATE.QUEUED || state === BULK_JOB_STATE.RUNNING;

export interface BulkJobSkippedRow {
  rowIndex?: number | null;
  /** The unit as handed over — its shape is the kind's (a guest import: an `ImportGuestRecipient`). */
  payload?: Record<string, any> | null;
  /** A code: `BULK_JOB_REASON.GAVE_UP` or the kind's own. */
  reason?: string | null;
}

export interface BulkJob {
  id: string;
  kind?: string | null;
  direction?: string | null;
  eventId?: string | null;
  jobState?: string | null;
  totalRows?: number | null;
  processedRows?: number | null;
  /** How many rows ended each way — the kind decides the words. */
  outcomeCounts?: Array<{ outcome?: string | null; count?: number | null }> | null;
  /** On a failed job a `BULK_JOB_REASON` code. While running, a technical note — never shown. */
  lastError?: string | null;
  startedAt?: string | null;
  finishedAt?: string | null;
  dismissedAt?: string | null;
  createdAt?: string | null;
  rights?: string[] | null;
  /** Only on the report read. */
  skippedRows?: BulkJobSkippedRow[] | null;
  /** Only on the report read: the records the job hands back (a guest import: the guests to select). */
  recordIds?: string[] | null;
}

export interface BulkJobFilter {
  id?: string[];
  kind?: string[];
  direction?: string[];
  eventId?: string[];
  jobState?: string[];
  /** Only the jobs whose report the host has not closed. */
  undismissed?: boolean;
}

/** How many rows of a job ended with `outcome`. */
export const bulkJobCount = (job: BulkJob | null | undefined, outcome: string): number =>
  Number(job?.outcomeCounts?.find((c) => c?.outcome === outcome)?.count) || 0;

export const BULK_JOB_FIELDS = `
  id
  kind
  direction
  eventId
  jobState
  totalRows
  processedRows
  outcomeCounts { outcome count }
  lastError
  startedAt
  finishedAt
  dismissedAt
  createdAt
  rights
`;

const LIST_QUERY = gql`
  query BulkJobList($filter: BulkJobFilter, $params: PaginationAndSorting) {
    bulkJobList(filter: $filter, params: $params) {
      code
      errors { name errors warnings }
      data { ${BULK_JOB_FIELDS} }
    }
  }
`;

const REPORT_QUERY = gql`
  query BulkJobReport($id: ID) {
    bulkJobGet(id: $id) {
      code
      errors { name errors warnings }
      data {
        ${BULK_JOB_FIELDS}
        recordIds
        skippedRows { rowIndex payload reason }
      }
    }
  }
`;

const DISMISS_MUTATION = gql`
  mutation BulkJobDismiss($params: BulkJobDismissInput) {
    bulkJobDismiss(params: $params) {
      code
      errors { name errors warnings }
      data { ${BULK_JOB_FIELDS} }
    }
  }
`;

export class BulkJobService {
  /** ⚠ `network-only`: this is polled while a job runs, and a cached answer would say "still running" for ever. */
  static async list(filter?: BulkJobFilter, pagination?: Pagination, sortBy?: SortItem[]): Promise<OpResult> {
    return ApiService.executeQuery(
      LIST_QUERY,
      { filter: filter || {}, params: { pagination: pagination || undefined, sortBy: sortBy || undefined } },
      { fetchPolicy: "network-only" },
    );
  }

  /**
   * The jobs of one kind (on one event, when given) whose report is still showing, newest first — ALL of them: a capped
   * read could hide one still running behind finished reports the host has not closed.
   */
  static async listFor(kind: BulkJobKind, eventId?: string | null): Promise<OpResult> {
    return listAllPages(BulkJobService.list, { kind: [kind], ...(eventId ? { eventId: [eventId] } : {}), undismissed: true });
  }

  /** One job with its refused rows and the records it hands back. */
  static async report(id: string): Promise<OpResult> {
    return ApiService.executeQuery(REPORT_QUERY, { id }, { fetchPolicy: "network-only" });
  }

  /** Close a finished job's report. */
  static async dismiss(id: string): Promise<OpResult> {
    return ApiService.executeMutation(DISMISS_MUTATION, { params: { id } });
  }
}
